// src/pages/Drawings.tsx
// Drawings & Specs AI Agent — platform module
// Sections 2–4, 8–9 of TrafficProcure Drawings & Specs specification

import { useState, useRef } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_DOCUMENTS, MOCK_TASKS, MOCK_BOM, MOCK_RFIS, MOCK_VARS, MOCK_REVISION_CHANGES, MOCK_MILESTONES,
  DOC_TYPE_META, DOC_STATUS_META, TASK_STATUS_META, COMPLIANCE_META, STOCK_STATUS_META,
  RFI_STATUS_META, VAR_STATUS_META, CHANGE_CLASS_META, LIKELIHOOD_META,
  DrawingDocument, DrawingTask, BOMItem, RFI, VariationRequest, RevisionChange,
  DocType, TaskStatus, VARStatus, RFIStatus, ChangeClassification,
  formatCost, getTasksForJob, getBOMForJob, getRFIsForJob, getVARsForJob,
} from '../utils/mockDataDrawings'
import { MOCK_JOBS } from '../utils/mockData'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import { formatDate } from '../utils/formatters'
import { MOCK_CONFIGURED_TASKS, ConfiguredTask } from '../utils/mockDataMobile'
import { buildDraftFromDrawingTask, PropagatedTaskDraft } from '../utils/propagateTasks'
import { PropagateTasksModal } from '../components/PropagateTasksModal'
import {
  applyVARtoBOM, getRFIProcurementBlocks, isVARAlreadyApplied, summariseVARImpact,
  type VARPropagationResult,
} from '../utils/variationPropagation'
import {
  FileText, Upload, CheckCircle, AlertTriangle, Clock, ChevronDown,
  ChevronRight, Plus, Send, X, Edit, Eye, Download, Flag, Zap, Shield,
  RotateCcw, RefreshCw, Smartphone, ArrowRight, FileSearch, Layers,
  ClipboardList, Activity, AlertCircle, Package,
  History, GitCompare, Bot,
} from 'lucide-react'
import { getDrawingPins, DRAWING_PIN_META, addAgentActivity, createIFCWIDraft } from '../utils/mockDataProjectGaps'

// ── Helpers ────────────────────────────────────────────────────────
function timeAgo(iso: string): string {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (d === 0) return 'Today'
  if (d === 1) return 'Yesterday'
  return `${d} days ago`
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
}

// ── Job selector ────────────────────────────────────────────────────
function JobSelector({ jobId, setJobId, scopedJobIds }: { jobId: string; setJobId: (j: string) => void; scopedJobIds: string[] | null }) {
  const jobs = scopedJobIds !== null
    ? MOCK_JOBS.filter(j => scopedJobIds.includes(j.id) && !['COMPLETED','CLOSED'].includes(j.status))
    : MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500">Job:</span>
      <select className="input text-sm w-auto" value={jobId} onChange={e => setJobId(e.target.value)}>
        {jobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber} — {j.description.split('—')[0].trim()}</option>)}
      </select>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 1 — DOCUMENT REGISTER
// ─────────────────────────────────────────────────────────────────
function DocumentsTab({ jobId, canEdit }: { jobId: string; canEdit: boolean }) {
  const [docs, setDocs]             = useState(MOCK_DOCUMENTS)
  const [showUpload, setShowUpload]  = useState(false)
  const [superseding, setSuperseding] = useState<string | null>(null)  // docId being superseded
  const [aiSimulating, setAiSimulating] = useState<string | null>(null)
  const [newDoc, setNewDoc]          = useState({ title: '', docType: 'DRAWING' as DocType, revisionNumber: 'A', notes: '' })
  const milestones = MOCK_MILESTONES.filter(m => m.jobId === jobId)

  const openNewRevision = (doc: typeof MOCK_DOCUMENTS[0]) => {
    const nextRev = String.fromCharCode((doc.revisionNumber.toUpperCase().charCodeAt(0)) + 1)
    setNewDoc({ title: doc.title, docType: doc.docType, revisionNumber: nextRev, notes: `Supersedes Rev ${doc.revisionNumber}` })
    setSuperseding(doc.id)
    setShowUpload(true)
  }

  const jobDocs = docs.filter(d => d.jobId === jobId)
  const active  = jobDocs.filter(d => d.status === 'APPROVED_ACTIVE').length
  const pending = jobDocs.filter(d => d.status === 'DRAFT_PENDING_REVIEW').length
  const processing = jobDocs.filter(d => d.status === 'PROCESSING').length

  // PM-04: Drawing viewer + IFC pipeline state
  const [viewerDoc, setViewerDoc] = useState<DrawingDocument | null>(null)
  const [ifcProcessing, setIfcProcessing] = useState<string | null>(null)
  const [ifcResult, setIfcResult] = useState<Record<string, { holdPoints: string[] }>>({})
  const [wiDraftGenerating, setWiDraftGenerating] = useState<string | null>(null)
  const [wiDraftGenerated, setWiDraftGenerated] = useState<Set<string>>(new Set())

  const handleSetIFC = (doc: DrawingDocument) => {
    if (ifcProcessing) return
    setIfcProcessing(doc.id)
    setTimeout(() => {
      const holdPoints = [
        `HP-01 — Subgrade compaction verification (MRTS50 cl. 6.3) — Hold Point`,
        `WP-02 — Pipe bedding layer inspection (MRTS11 cl. 5.4) — Witness Point`,
        `HP-03 — Concrete pour commencement (MRTS73 cl. 4.2) — Hold Point`,
      ]
      setIfcResult(prev => ({ ...prev, [doc.id]: { holdPoints } }))
      setIfcProcessing(null)
      addAgentActivity({
        jobId: doc.jobId,
        agentCode: 'A09', agentName: 'QA Specialist', agentWave: 'Wave 1',
        action: `Drawing IFC reviewed — ${holdPoints.length} hold points generated`,
        detail: `Reviewed ${doc.title} (IFC). Drew on MRTS50, MRTS11, MRTS73. Generated ${holdPoints.length} inspection points (2 HP, 1 WP). Linked to itp_hold_points register.`,
        confidence: 'HIGH', autonomyLevel: 2, timestamp: new Date().toISOString(),
        humanResponse: 'PENDING', linkedModule: 'Drawings', linkedRecordId: doc.id,
      })
    }, 2000)
  }

  // Simulate AI analysis
  const simulateAI = (docId: string) => {
    setAiSimulating(docId)
    setDocs(prev => prev.map(d => d.id === docId ? { ...d, status: 'PROCESSING' } : d))
    setTimeout(() => {
      setDocs(prev => prev.map(d => d.id === docId ? {
        ...d, status: 'DRAFT_PENDING_REVIEW',
        aiProcessedAt: new Date().toISOString(),
        tasksExtracted: Math.floor(Math.random() * 12) + 4,
        bomItemsExtracted: Math.floor(Math.random() * 7) + 2,
      } : d))
      setAiSimulating(null)
    }, 3000)
  }

  const approveDoc = (docId: string) => {
    setDocs(prev => prev.map(d => d.id === docId ? { ...d, status: 'APPROVED_ACTIVE' } : d))
  }

  const handleUpload = () => {
    const job = MOCK_JOBS.find(j => j.id === jobId)
    const newId = `doc_${Date.now()}`
    setDocs(prev => [
      // If superseding, mark old doc as SUPERSEDED and link it
      ...prev.map(d => d.id === superseding ? { ...d, status: 'SUPERSEDED' as const, supersededById: newId } : d),
      {
        id: newId, jobId, jobNumber: job?.jobNumber ?? '', title: newDoc.title,
        docType: newDoc.docType, status: 'UPLOADED' as const, revisionNumber: newDoc.revisionNumber,
        pages: null, fileSizeMb: 0, format: 'PDF', discipline: 'All',
        uploadedById: 'u_current', uploadedByName: 'Current User', uploadedAt: new Date().toISOString(),
        aiProcessedAt: null, tasksExtracted: 0, bomItemsExtracted: 0, scheduleConflictCount: 0,
        supersededById: null, notes: newDoc.notes || null,
      }
    ])
    setShowUpload(false)
    setSuperseding(null)
    setNewDoc({ title: '', docType: 'DRAWING', revisionNumber: 'A', notes: '' })
  }

  return (
    <div className="space-y-5">
      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total documents', value: jobDocs.length, bg: 'bg-white' },
          { label: 'Approved active',  value: active,     bg: 'bg-green-50', text: 'text-green-700' },
          { label: 'Pending review',   value: pending,    bg: pending > 0 ? 'bg-amber-50' : 'bg-white', text: pending > 0 ? 'text-amber-700' : 'text-gray-900' },
          { label: 'AI processing',    value: processing, bg: processing > 0 ? 'bg-blue-50' : 'bg-white', text: processing > 0 ? 'text-blue-700' : 'text-gray-900' },
        ].map(c => (
          <div key={c.label} className={`border border-gray-200 rounded-xl p-4 ${c.bg}`}>
            <div className={`text-2xl font-bold ${c.text ?? 'text-gray-900'}`}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Works schedule milestones */}
      {milestones.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b bg-teal-50 flex items-center gap-2">
            <Clock className="w-4 h-4 text-teal-600" />
            <h3 className="font-semibold text-teal-900 text-sm">Works schedule milestones (AI extracted)</h3>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 px-5 py-3">
            {milestones.map(ms => (
              <div key={ms.id} className="flex items-center gap-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${ms.type === 'HOLD_POINT' ? 'bg-amber-500' : ms.type === 'PRACTICAL_COMPLETION' ? 'bg-green-500' : 'bg-blue-400'}`} />
                <span className="text-gray-600">{ms.label}</span>
                <span className="font-semibold text-gray-900">{fmtDate(ms.date)}</span>
                {ms.conflictingTaskIds.length > 0 && (
                  <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">⚠ Task conflict</span>
                )}
                {ms.confirmed && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload button */}
      {canEdit && (
        <div className="flex justify-end">
          <button onClick={() => setShowUpload(true)} className="btn-primary flex items-center gap-2 text-sm">
            <Upload className="w-4 h-4" />Upload document
          </button>
        </div>
      )}

      {/* Upload form */}
      {showUpload && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-blue-900 text-sm">
                {superseding ? `Upload new revision — supersedes ${docs.find(d => d.id === superseding)?.title ?? ''} Rev ${docs.find(d => d.id === superseding)?.revisionNumber}` : 'Upload new document'}
              </h4>
              {superseding && (
                <p className="text-xs text-purple-700 mt-0.5">Previous revision will be auto-marked SUPERSEDED on upload.</p>
              )}
            </div>
            <button onClick={() => { setShowUpload(false); setSuperseding(null) }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Document title <span className="text-red-500">*</span></label>
              <input className="input text-sm" value={newDoc.title} onChange={e => setNewDoc(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Signal Layout Plan Rev C" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Document type <span className="text-red-500">*</span></label>
              <select className="input text-sm" value={newDoc.docType} onChange={e => setNewDoc(p => ({ ...p, docType: e.target.value as DocType }))}>
                {Object.entries(DOC_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Revision number</label>
              <input className="input text-sm" value={newDoc.revisionNumber} onChange={e => setNewDoc(p => ({ ...p, revisionNumber: e.target.value }))} placeholder="e.g. A, B, 2.1" />
            </div>
          </div>
          <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center text-sm text-gray-500">
            <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
            <p>Drag PDF, DWG, DOCX, or XLSX here, or <span className="text-blue-600 cursor-pointer underline">browse</span></p>
            <p className="text-xs text-gray-400 mt-1">Max 50MB · Supported: PDF, DWG, DXF, DOCX, XLSX, MPP</p>
          </div>
          <div className="bg-blue-100 rounded-lg px-3 py-2 text-xs text-blue-800">
            <Zap className="w-3.5 h-3.5 inline mr-1" />
            AI agent will automatically extract tasks and BOM items after upload. Processing typically takes 3–8 minutes.
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowUpload(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={handleUpload} disabled={!newDoc.title} className="btn-primary text-sm disabled:opacity-50">Upload & queue for AI analysis</button>
          </div>
        </div>
      )}

      {/* Document list */}
      <div className="space-y-3">
        {jobDocs.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl py-14 text-center text-gray-400">
            <FileSearch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No documents uploaded for this job yet</p>
          </div>
        )}
        {jobDocs.map(doc => {
          const tm = DOC_TYPE_META[doc.docType]
          const sm = DOC_STATUS_META[doc.status]
          const isProcessing = doc.status === 'PROCESSING' || aiSimulating === doc.id
          return (
            <div key={doc.id} className={`bg-white border rounded-xl overflow-hidden ${doc.status === 'DRAFT_PENDING_REVIEW' ? 'border-amber-300' : 'border-gray-200'}`}>
              <div className="px-5 py-4 flex items-start gap-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg flex-shrink-0 ${tm.bg}`}>
                  {tm.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-gray-900 text-sm">{doc.title}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${tm.bg} ${tm.text}`}>{tm.label}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">Rev {doc.revisionNumber}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${sm.bg} ${sm.text}`}>
                      {isProcessing && <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse inline-block" />}
                      {sm.label}
                    </span>
                    {doc.scheduleConflictCount > 0 && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">⚠ {doc.scheduleConflictCount} schedule conflict{doc.scheduleConflictCount !== 1 ? 's' : ''}</span>}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-3">
                    {doc.pages && <span>{doc.pages} pages</span>}
                    <span>{doc.fileSizeMb > 0 ? `${doc.fileSizeMb} MB` : 'Uploading…'}</span>
                    <span>{doc.format}</span>
                    <span>Uploaded {doc.uploadedByName} · {timeAgo(doc.uploadedAt)}</span>
                    {doc.aiProcessedAt && doc.tasksExtracted > 0 && (
                      <span className="text-blue-600">AI: {doc.tasksExtracted} tasks, {doc.bomItemsExtracted} BOM items extracted</span>
                    )}
                    {doc.notes && <span className="text-gray-400 italic">"{doc.notes.slice(0, 60)}{doc.notes.length > 60 ? '…' : ''}"</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {doc.status === 'UPLOADED' && canEdit && (
                    <button onClick={() => simulateAI(doc.id)} className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1.5 rounded-lg font-medium">
                      <Zap className="w-3.5 h-3.5" />Run AI analysis
                    </button>
                  )}
                  {doc.status === 'DRAFT_PENDING_REVIEW' && canEdit && (
                    <>
                      <button onClick={() => approveDoc(doc.id)} className="flex items-center gap-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-3 py-1.5 rounded-lg font-medium">
                        <CheckCircle className="w-3.5 h-3.5" />Approve
                      </button>
                      <button className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 px-3 py-1.5 rounded-lg font-medium">
                        <Eye className="w-3.5 h-3.5" />Review AI output
                      </button>
                    </>
                  )}
                  {doc.status === 'APPROVED_ACTIVE' && !ifcResult[doc.id] && (
                    <button
                      onClick={() => handleSetIFC(doc)}
                      disabled={ifcProcessing === doc.id}
                      className="flex items-center gap-1 text-xs bg-teal-100 text-teal-700 hover:bg-teal-200 px-3 py-1.5 rounded-lg font-medium disabled:opacity-60">
                      {ifcProcessing === doc.id
                        ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />A09 analysing…</>
                        : <><Bot className="w-3.5 h-3.5" />Generate ITP hold points</>}
                    </button>
                  )}
                  {doc.status === 'APPROVED_ACTIVE' && canEdit && (
                    <button onClick={() => openNewRevision(doc)}
                      className="flex items-center gap-1 text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1.5 rounded-lg font-medium">
                      <History className="w-3.5 h-3.5" />New revision
                    </button>
                  )}
                  {isProcessing && (
                    <div className="flex items-center gap-1.5 text-xs text-blue-600">
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />Analysing…
                    </div>
                  )}
                  <button onClick={() => setViewerDoc(doc)} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-lg font-medium">
                    <Eye className="w-3.5 h-3.5" />View
                  </button>
                  <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* PM-04: A09 IFC hold-points panel */}
      {Object.entries(ifcResult).map(([docId, result]) => {
        const doc = docs.find(d => d.id === docId)
        if (!doc) return null
        return (
          <div key={docId} className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-semibold text-teal-900">A09 QA Specialist — ITP hold points generated</span>
                <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full">Assisted (Level 2)</span>
              </div>
              <span className="text-xs text-gray-500">{doc.title} · Rev {doc.revisionNumber}</span>
            </div>
            <div className="space-y-1.5">
              {result.holdPoints.map((hp, i) => (
                <div key={i} className="flex items-center gap-2 bg-white border border-teal-200 rounded-lg px-3 py-2 text-xs">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${hp.includes('Hold Point') ? 'bg-orange-500' : 'bg-blue-500'}`}>
                    {hp.includes('Hold Point') ? 'H' : 'W'}
                  </span>
                  <span className="text-gray-800">{hp}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button className="text-xs bg-teal-600 text-white hover:bg-teal-700 px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />Accept &amp; add to ITP register
              </button>
              <button className="text-xs bg-white border border-teal-200 text-teal-700 hover:bg-teal-50 px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
                <Edit className="w-3.5 h-3.5" />Review &amp; modify
              </button>
              <p className="text-xs text-gray-500 ml-auto">Human review required before committing to ITP</p>
            </div>
          </div>
        )
      })}

      {/* PM-05: IFC → WI Pipeline — Draft Work Instruction from this drawing */}
      {Object.entries(ifcResult).map(([docId, result]) => {
        const doc = docs.find(d => d.id === docId)
        if (!doc || result.holdPoints.length === 0) return null
        const alreadyGenerated = wiDraftGenerated.has(docId)
        const generating = wiDraftGenerating === docId
        const jobTasks = MOCK_TASKS.filter(t => t.jobId === doc.jobId && t.level !== 1).map(t => t.title)
        return (
          <div key={`wi_${docId}`} className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Bot className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-semibold text-indigo-900">A09 QA Specialist — Draft Work Instruction ready</span>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">PM-05</span>
                </div>
                <p className="text-xs text-indigo-700">
                  {result.holdPoints.length} ITP inspection point{result.holdPoints.length !== 1 ? 's' : ''} + {Math.min(jobTasks.length, 3)} extracted task{Math.min(jobTasks.length, 3) !== 1 ? 's' : ''} ready to assemble into a field work package.
                </p>
              </div>
              <div className="flex-shrink-0">
                {alreadyGenerated
                  ? <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />Draft sent to Work Instructions
                    </span>
                  : <button
                      onClick={() => {
                        setWiDraftGenerating(docId)
                        setTimeout(() => {
                          createIFCWIDraft(doc, result.holdPoints, jobTasks)
                          setWiDraftGenerating(null)
                          setWiDraftGenerated(prev => new Set([...prev, docId]))
                        }, 1200)
                      }}
                      disabled={generating}
                      className="text-xs bg-indigo-600 text-white hover:bg-indigo-700 px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5 disabled:opacity-60">
                      {generating
                        ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />A09 building WI…</>
                        : <><Zap className="w-3.5 h-3.5" />Generate Work Instruction draft</>}
                    </button>
                }
              </div>
            </div>
          </div>
        )
      })}

      {/* PM-04: Drawing Viewer slide-over */}
      {viewerDoc && (
        <DrawingViewerPanel
          doc={viewerDoc}
          onClose={() => setViewerDoc(null)}
          canEdit={canEdit}
          ifcHoldPoints={ifcResult[viewerDoc.id]?.holdPoints ?? []}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 2 — TASK LIST
// ─────────────────────────────────────────────────────────────────
// PM-04: Drawing Viewer Panel (slide-over)
function DrawingViewerPanel({
  doc, onClose, canEdit, ifcHoldPoints,
}: {
  doc: DrawingDocument
  onClose: () => void
  canEdit: boolean
  ifcHoldPoints: string[]
}) {
  const pins = getDrawingPins(doc.id)
  const [showRevHistory, setShowRevHistory] = useState(true)

  const firstRev = String.fromCharCode(Math.max(doc.revisionNumber.charCodeAt(0) - 1, 65))
  const revHistory = [
    { rev: doc.revisionNumber, date: doc.uploadedAt, byName: doc.uploadedByName, notes: 'Current revision', isCurrent: true },
    ...(doc.revisionNumber !== 'A'
      ? [{ rev: firstRev, date: '2026-06-01T00:00:00Z', byName: 'Sarah Lee', notes: 'Revised alignment at Ch.0+480', isCurrent: false }]
      : []),
    { rev: 'A', date: '2026-05-15T00:00:00Z', byName: 'Jane PM', notes: 'Initial issue for review', isCurrent: doc.revisionNumber === 'A' },
  ].filter((r, i, arr) => arr.findIndex(x => x.rev === r.rev) === i)

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/50" />
      <div className="w-full max-w-5xl bg-white flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-900">
          <div className="flex items-center gap-3 min-w-0">
            <Layers className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{doc.title}</p>
              <p className="text-xs text-gray-400">Rev {doc.revisionNumber} · {doc.format} · {doc.pages ?? '?'} pages · {doc.status === 'APPROVED_ACTIVE' ? 'IFC ✓' : doc.status}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRevHistory(!showRevHistory)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium ${
                showRevHistory ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}>
              <History className="w-3.5 h-3.5" />Revisions ({revHistory.length})
            </button>
            <button className="flex items-center gap-1.5 text-xs bg-gray-700 text-gray-200 hover:bg-gray-600 px-2.5 py-1.5 rounded-lg">
              <Download className="w-3.5 h-3.5" />Download
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Drawing canvas */}
          <div className="flex-1 bg-gray-100 relative overflow-hidden flex flex-col">
            {/* Pin layer toolbar */}
            <div className="flex items-center gap-2 px-4 py-2 bg-white border-b text-xs flex-wrap">
              <span className="text-gray-500">Pin layers:</span>
              {Object.entries(DRAWING_PIN_META).map(([type, meta]) => (
                <span key={type} className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                  <span className={`w-2 h-2 rounded-full ${meta.bg}`} />{meta.label}
                </span>
              ))}
              <span className="ml-auto text-gray-400">{pins.length} pin{pins.length !== 1 ? 's' : ''} on this drawing</span>
            </div>

            {/* SVG mock drawing canvas */}
            <div className="flex-1 relative overflow-hidden">
              <svg className="w-full h-full" viewBox="0 0 800 520" xmlns="http://www.w3.org/2000/svg">
                {/* Paper */}
                <rect width="800" height="520" fill="#f5f0e8" />
                {/* Border */}
                <rect x="10" y="10" width="780" height="430" fill="none" stroke="#333" strokeWidth="2" />
                <rect x="15" y="15" width="770" height="420" fill="none" stroke="#999" strokeWidth="0.5" />
                {/* Title block */}
                <rect x="0" y="450" width="800" height="70" fill="#fff" stroke="#333" strokeWidth="1.5" />
                <line x1="200" y1="450" x2="200" y2="520" stroke="#333" strokeWidth="1" />
                <line x1="500" y1="450" x2="500" y2="520" stroke="#333" strokeWidth="1" />
                <line x1="650" y1="450" x2="650" y2="520" stroke="#333" strokeWidth="1" />
                <text x="10" y="467" fontFamily="monospace" fontSize="9" fill="#555">MCB CIVIL ELECTRICAL PTY LTD</text>
                <text x="10" y="481" fontFamily="monospace" fontSize="8" fill="#333">ABN 44 128 993 087</text>
                <text x="210" y="467" fontFamily="Arial" fontSize="11" fontWeight="bold" fill="#1B3A6B">{doc.title.slice(0, 38)}</text>
                <text x="210" y="481" fontFamily="monospace" fontSize="9" fill="#555">{doc.jobId.toUpperCase()} · {doc.discipline ?? 'Civil'} Works</text>
                <text x="510" y="467" fontFamily="monospace" fontSize="9" fill="#555">DWG-{doc.jobId.toUpperCase()}-001</text>
                <text x="690" y="495" fontFamily="Arial" fontSize="22" fontWeight="bold" fill="#1B3A6B">{doc.revisionNumber}</text>
                {/* Placeholder content label */}
                <text x="400" y="220" fontFamily="Arial" fontSize="13" fill="#bbb" textAnchor="middle">PDF drawing renders here</text>
                <text x="400" y="238" fontFamily="Arial" fontSize="10" fill="#ccc" textAnchor="middle">{doc.format} · {doc.pages ?? '?'} pages · Served from Cloudflare R2</text>
                {/* Stylised road */}
                <rect x="60" y="70" width="680" height="38" fill="#d0cfc5" stroke="#999" strokeWidth="1" />
                <line x1="60" y1="89" x2="740" y2="89" stroke="#fff" strokeWidth="2" strokeDasharray="20,10" />
                {/* Signal poles */}
                {[320, 500, 660].map(x => (
                  <g key={x}>
                    <circle cx={x} cy="185" r="8" fill="none" stroke="#e08" strokeWidth="2" />
                    <line x1={x} y1="70" x2={x} y2="177" stroke="#e08" strokeWidth="1.5" />
                  </g>
                ))}
                {/* Chainage labels */}
                {[{x:80,l:'Ch. 0+000'},{x:300,l:'Ch. 0+240'},{x:480,l:'Ch. 0+420'},{x:640,l:'Ch. 0+600'}].map(c => (
                  <text key={c.l} x={c.x} y="65" fontFamily="monospace" fontSize="8" fill="#666">{c.l}</text>
                ))}
                {/* Cable route */}
                <path d="M 80 340 Q 200 330 320 340 Q 440 350 560 340 Q 640 332 740 340" fill="none" stroke="#2563eb" strokeWidth="2" strokeDasharray="8,4" />
                <text x="300" y="358" fontFamily="monospace" fontSize="8" fill="#2563eb">3C+E 6mm² Cu cable route (indicative)</text>
                {/* Work zone */}
                <rect x="80" y="145" width="200" height="120" fill="none" stroke="#7ba7bc" strokeWidth="1.5" strokeDasharray="5,3" />
                <text x="180" y="210" fontFamily="monospace" fontSize="8" fill="#7ba7bc" textAnchor="middle">WORK ZONE</text>
                {/* Notes box */}
                <rect x="530" y="145" width="200" height="120" fill="#fffde7" stroke="#ccc" strokeWidth="1" />
                <text x="540" y="162" fontFamily="monospace" fontSize="7" fill="#333">GENERAL NOTES:</text>
                {['1. All works per MRTS specs', '2. Refer SWMS before commencing', '3. Traffic management per TMP-001', '4. All dims in millimetres', `5. Status: ${doc.status === 'APPROVED_ACTIVE' ? 'IFC APPROVED' : 'DRAFT'}`].map((n, i) => (
                  <text key={i} x="540" y={176 + i * 13} fontFamily="monospace" fontSize="6.5" fill="#555">{n}</text>
                ))}
              </svg>

              {/* Interactive pins overlay */}
              {pins.map(pin => {
                const meta = DRAWING_PIN_META[pin.type]
                return (
                  <div key={pin.id}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                    style={{ left: `${pin.xPct}%`, top: `${pin.yPct}%` }}>
                    <div className={`w-6 h-6 rounded-full ${meta.bg} border-2 border-white shadow-lg flex items-center justify-center text-white text-[9px] font-bold`}>
                      {pin.type === 'WI' ? 'W' : pin.type === 'ITP_HOLD' ? 'H' : pin.type === 'QA_PASS' ? '✓' : pin.type === 'ISSUE' ? '!' : '✗'}
                    </div>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                      <strong>{pin.entityLabel}</strong> · {meta.label}
                      {pin.entityStatus && <span className="ml-1 text-gray-300">· {pin.entityStatus}</span>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Revision history sidebar */}
          {showRevHistory && (
            <div className="w-72 flex-shrink-0 border-l bg-white flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
                <History className="w-4 h-4 text-gray-500" />
                <h3 className="font-semibold text-gray-900 text-sm">Revision history</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {revHistory.map((rev, i) => (
                  <div key={i} className={`rounded-xl border p-3 ${rev.isCurrent ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-bold ${rev.isCurrent ? 'text-blue-700' : 'text-gray-700'}`}>Rev {rev.rev}</span>
                      {rev.isCurrent && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Current</span>}
                    </div>
                    <p className="text-xs text-gray-600">{rev.notes}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(rev.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })} · {rev.byName}
                    </p>
                    {!rev.isCurrent && (
                      <button className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <GitCompare className="w-3 h-3" />Compare with current
                      </button>
                    )}
                  </div>
                ))}

                {canEdit && (
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center">
                    <Upload className="w-5 h-5 mx-auto text-gray-300 mb-1" />
                    <p className="text-xs text-gray-500 font-medium">Upload new revision</p>
                    <p className="text-xs text-gray-400">Previous auto-marked SUPERSEDED</p>
                    <button className="mt-2 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1 rounded-lg font-medium">Choose file</button>
                  </div>
                )}

                {ifcHoldPoints.length > 0 && (
                  <div className="border-t pt-3 mt-3 space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-800">
                      <Bot className="w-3.5 h-3.5 text-teal-600" />A09 ITP hold points
                    </div>
                    {ifcHoldPoints.map((hp, i) => (
                      <div key={i} className="text-xs bg-teal-50 border border-teal-100 rounded-lg px-2.5 py-1.5 text-teal-800">
                        {hp.split(' — ')[0]}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TaskRow({ task, depth, tasks, canEdit, onApprove, onPushMobile, onEdit }: {
  task: DrawingTask; depth: number; tasks: DrawingTask[]
  canEdit: boolean; onApprove: (id: string) => void; onPushMobile: (id: string) => void; onEdit: (t: DrawingTask) => void
}) {
  const [expanded, setExpanded] = useState(depth < 2)
  const children = tasks.filter(t => t.parentId === task.id)
  const sm = TASK_STATUS_META[task.status]
  const indent = depth * 20

  return (
    <>
      <div
        className={`flex items-start gap-3 px-5 py-3 border-b last:border-0 hover:bg-gray-50/50 ${task.status === 'BLOCKED' ? 'bg-red-50/30' : ''}`}
        style={{ paddingLeft: `${20 + indent}px` }}
      >
        {/* Expand toggle */}
        <button onClick={() => setExpanded(!expanded)} className="mt-0.5 text-gray-400 flex-shrink-0">
          {children.length > 0
            ? (expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)
            : <div className="w-4" />
          }
        </button>

        {/* Status dot */}
        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${sm.dot}`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-gray-400">{task.taskNumber}</span>
            <span className={`font-semibold text-gray-900 ${task.level === 1 ? 'text-sm' : 'text-sm'}`}>{task.title}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{task.discipline}</span>
            {task.source === 'AI' && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />AI</span>}
            {task.source === 'MANUAL' && <span className="text-xs bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-full">Manual</span>}
            {task.holdPointRef && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">{task.holdPointRef}</span>}
            {task.publishedToMobile && <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Smartphone className="w-2.5 h-2.5" />On mobile</span>}
            {task.whsFlags.map(f => (
              <span key={f} className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                <Shield className="w-2.5 h-2.5" />{f.replace(/_/g,' ')}
              </span>
            ))}
          </div>
          <div className="text-xs text-gray-400 mt-0.5 flex gap-3">
            <span>{task.estimatedDuration}</span>
            {task.assignedToName && <span>→ {task.assignedToName}</span>}
            {task.bomItemIds.length > 0 && <span>{task.bomItemIds.length} BOM item{task.bomItemIds.length !== 1 ? 's' : ''}</span>}
            {task.sourcePageRef && <span className="text-blue-400">{task.sourcePageRef}</span>}
            {task.requiredCredentials.length > 0 && <span>Requires: {task.requiredCredentials.join(', ')}</span>}
          </div>
          {task.status === 'BLOCKED' && task.taskNumber === '1.1.2' && (
            <div className="mt-1 text-xs text-red-700 bg-red-50 rounded px-2 py-1 inline-block">
              ⛔ Blocked — awaiting RFI-TP001-001 response (foundation depth query)
            </div>
          )}
        </div>

        {canEdit && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {task.status === 'DRAFT' && (
              <button onClick={() => onApprove(task.id)} className="text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1 rounded-lg font-medium transition-colors">
                Approve
              </button>
            )}
            {task.status === 'APPROVED' && !task.publishedToMobile && (
              <button onClick={() => onPushMobile(task.id)} className="text-xs bg-teal-100 text-teal-700 hover:bg-teal-200 px-2.5 py-1 rounded-lg font-medium transition-colors flex items-center gap-1">
                <Smartphone className="w-3 h-3" />Push
              </button>
            )}
            <button onClick={() => onEdit(task)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
              <Edit className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {expanded && children.map(child => (
        <TaskRow key={child.id} task={child} depth={depth + 1} tasks={tasks} canEdit={canEdit} onApprove={onApprove} onPushMobile={onPushMobile} onEdit={onEdit} />
      ))}
    </>
  )
}

function TasksTab({ jobId, canEdit }: { jobId: string; canEdit: boolean }) {
  const [tasks, setTasks]   = useState<DrawingTask[]>(MOCK_TASKS)
  const [editTask, setEditTask] = useState<DrawingTask | null>(null)
  const [showAdd, setShowAdd]   = useState(false)
  const [newTask, setNewTask]   = useState({ title: '', discipline: 'Civil', estimatedDuration: '1 day', assignedToId: '', notes: '' })
  const [filter, setFilter]     = useState<TaskStatus | 'ALL'>('ALL')
  // Gap 1 — Propagate bridge state
  const [propagateDraft, setPropagateDraft] = useState<PropagatedTaskDraft | null>(null)
  const [configuredTasks, setConfiguredTasks] = useState<ConfiguredTask[]>(MOCK_CONFIGURED_TASKS)

  const jobTasks = tasks.filter(t => t.jobId === jobId)
  const phaseTasks = jobTasks.filter(t => t.level === 1)
  const stats = {
    total:       jobTasks.length,
    approved:    jobTasks.filter(t => t.status === 'APPROVED').length,
    inProgress:  jobTasks.filter(t => t.status === 'IN_PROGRESS').length,
    complete:    jobTasks.filter(t => t.status === 'COMPLETE').length,
    draft:       jobTasks.filter(t => t.status === 'DRAFT').length,
    blocked:     jobTasks.filter(t => t.status === 'BLOCKED').length,
  }
  const onMobile = jobTasks.filter(t => t.publishedToMobile).length

  const approveTask = (id: string) => setTasks(prev => prev.map(t => t.id === id ? { ...t, status: 'APPROVED' } : t))
  // Gap 1 — Push opens PropagateTasksModal instead of a direct local state flip
  const pushMobile = (id: string) => {
    const task = tasks.find(t => t.id === id)
    if (!task) return
    const draft = buildDraftFromDrawingTask(task)
    setPropagateDraft(draft)
  }

  // Called when PM confirms the draft in PropagateTasksModal
  const handleCommitDraft = (committed: ConfiguredTask) => {
    setConfiguredTasks(prev => [...prev, committed])
    setTasks(prev => prev.map(t =>
      t.id === committed.drawingTaskId ? { ...t, publishedToMobile: true } : t
    ))
    setPropagateDraft(null)
  }

  const addTask = () => {
    const emp = MOCK_EMPLOYEES.find(e => e.id === newTask.assignedToId)
    setTasks(prev => [...prev, {
      id: `tsk_${Date.now()}`, jobId, level: 2, parentId: phaseTasks[0]?.id ?? null,
      taskNumber: `${phaseTasks.length + 1}.${(jobTasks.filter(t => t.level === 2).length) + 1}`,
      title: newTask.title, description: newTask.notes || null,
      source: 'MANUAL', sourceDocId: null, sourcePageRef: null,
      discipline: newTask.discipline as any, estimatedDuration: newTask.estimatedDuration,
      requiredCredentials: [], bomItemIds: [], prerequisiteTaskIds: [], holdPointRef: null,
      status: 'DRAFT', assignedToId: newTask.assignedToId || null, assignedToName: emp?.fullName ?? null,
      publishedToMobile: false, whsFlags: [], specClauseRef: null,
    }])
    setShowAdd(false)
    setNewTask({ title: '', discipline: 'Civil', estimatedDuration: '1 day', assignedToId: '', notes: '' })
  }

  const filteredPhases = filter === 'ALL' ? phaseTasks : phaseTasks  // phase always shown; sub-tasks filtered visually

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {[
            ['ALL', `All (${stats.total})`], ['DRAFT', `Draft (${stats.draft})`],
            ['APPROVED', `Approved (${stats.approved})`], ['IN_PROGRESS', `In progress (${stats.inProgress})`],
            ['COMPLETE', `Complete (${stats.complete})`], ['BLOCKED', `Blocked (${stats.blocked})`],
          ].map(([f, l]) => (
            <button key={f} onClick={() => setFilter(f as any)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filter === f ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-1">
            <Smartphone className="w-3 h-3 inline mr-1" />{onMobile} on mobile
          </span>
          {canEdit && (
            <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" />Add task
            </button>
          )}
        </div>
      </div>

      {/* Add task form */}
      {showAdd && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 space-y-3">
          <h4 className="font-semibold text-purple-900 text-sm">Add manual task</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Task title <span className="text-red-500">*</span></label>
              <input className="input text-sm" value={newTask.title} onChange={e => setNewTask(p => ({ ...p, title: e.target.value }))} placeholder="Plain-language task name…" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Discipline</label>
              <select className="input text-sm" value={newTask.discipline} onChange={e => setNewTask(p => ({ ...p, discipline: e.target.value }))}>
                {['Civil','Electrical','HV','Traffic Control','Commissioning','Other'].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Estimated duration</label>
              <input className="input text-sm" value={newTask.estimatedDuration} onChange={e => setNewTask(p => ({ ...p, estimatedDuration: e.target.value }))} placeholder="e.g. 2 days, 4 hours" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Assign to</label>
              <select className="input text-sm" value={newTask.assignedToId} onChange={e => setNewTask(p => ({ ...p, assignedToId: e.target.value }))}>
                <option value="">All crew on job</option>
                {MOCK_EMPLOYEES.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes / instructions</label>
              <input className="input text-sm" value={newTask.notes} onChange={e => setNewTask(p => ({ ...p, notes: e.target.value }))} placeholder="Optional details for field crew…" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={addTask} disabled={!newTask.title} className="btn-primary text-sm disabled:opacity-50">Add task</button>
          </div>
        </div>
      )}

      {/* Task hierarchy */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-500">
          <div className="col-span-6">Task</div>
          <div className="col-span-2">Duration</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div>
          {filteredPhases.length === 0
            ? <div className="py-12 text-center text-gray-400 text-sm">No tasks for this job yet</div>
            : filteredPhases.map(phase => (
                <TaskRow key={phase.id} task={phase} depth={0}
                  tasks={tasks.filter(t => t.jobId === jobId)}
                  canEdit={canEdit} onApprove={approveTask}
                  onPushMobile={pushMobile} onEdit={setEditTask} />
              ))
          }
        </div>
      </div>

      {/* WHS integration note */}
      {jobTasks.some(t => t.whsFlags.length > 0) && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-5 py-3 flex items-start gap-3">
          <Shield className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-orange-800">
            <strong>{jobTasks.filter(t => t.whsFlags.length > 0).length} tasks</strong> have WHS flags
            ({jobTasks.flatMap(t => t.whsFlags).filter((v, i, a) => a.indexOf(v) === i).join(', ')}).
            Draft SWMS items have been created — review in the Safety module before mobilising crew.
          </p>
        </div>
      )}

      {/* Gap 1 — PropagateTasksModal: PM reviews auto-populated ConfiguredTask before push */}
      {propagateDraft && (
        <PropagateTasksModal
          draft={propagateDraft}
          onCommit={handleCommitDraft}
          onClose={() => setPropagateDraft(null)}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 3 — BOM
// ─────────────────────────────────────────────────────────────────
function BOMTab({ jobId, canEdit }: { jobId: string; canEdit: boolean }) {
  const [bom, setBom]     = useState<BOMItem[]>(MOCK_BOM)
  const [cfFilter, setCfFilter] = useState<'ALL' | 'COMPLIANT' | 'NEEDS_REVIEW' | 'NON_COMPLIANT'>('ALL')
  const [showAdd, setShowAdd] = useState(false)
  const [newItem, setNewItem] = useState({ description: '', specRef: '', quantity: 1, unit: 'each', supplierRef: '', unitCostAud: 0 })

  const jobBom = bom.filter(b => b.jobId === jobId)
  const filtered = cfFilter === 'ALL' ? jobBom : jobBom.filter(b => b.complianceFlag === cfFilter)

  const totals = {
    compliant:    jobBom.filter(b => b.complianceFlag === 'COMPLIANT').length,
    needsReview:  jobBom.filter(b => b.complianceFlag === 'NEEDS_REVIEW').length,
    nonCompliant: jobBom.filter(b => b.complianceFlag === 'NON_COMPLIANT').length,
  }
  const totalCost = jobBom.filter(b => b.complianceFlag !== 'NON_COMPLIANT').reduce((s, b) => s + b.quantity * b.unitCostAud, 0)

  const addItem = () => {
    setBom(prev => [...prev, {
      id: `bom_${Date.now()}`, jobId, ...newItem, complianceFlag: 'NEEDS_REVIEW',
      complianceNote: 'Newly added — Procurement AI compliance check pending.',
      stockStatus: 'NOT_AVAILABLE', taskId: null, taskTitle: null,
      discipline: 'Other', sourceDocId: null, whs: null,
    }])
    setShowAdd(false)
    setNewItem({ description: '', specRef: '', quantity: 1, unit: 'each', supplierRef: '', unitCostAud: 0 })
  }

  return (
    <div className="space-y-4">
      {/* Compliance summary */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {[
            ['ALL', `All (${jobBom.length})`],
            ['COMPLIANT', `Compliant (${totals.compliant})`],
            ['NEEDS_REVIEW', `Needs review (${totals.needsReview})`],
            ['NON_COMPLIANT', `Non-compliant (${totals.nonCompliant})`],
          ].map(([f, l]) => (
            <button key={f} onClick={() => setCfFilter(f as any)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium ${cfFilter === f ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{l}
            </button>
          ))}
        </div>
        {canEdit && (
          <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" />Add BOM item
          </button>
        )}
      </div>

      {/* Non-compliant alert */}
      {totals.nonCompliant > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800">
            <strong>{totals.nonCompliant} non-compliant item{totals.nonCompliant !== 1 ? 's' : ''}</strong> detected by Procurement AI.
            POs cannot be raised for these items without PM override. Review and substitute.
          </p>
        </div>
      )}

      {/* Add item form */}
      {showAdd && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 space-y-3">
          <h4 className="font-semibold text-purple-900 text-sm">Add BOM item (manual)</h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
            <Zap className="w-3.5 h-3.5 inline mr-1" />
            Procurement AI will cross-reference this item against the supplier catalogue, client specs, and applicable Australian Standards after saving.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
              <input className="input text-sm" value={newItem.description} onChange={e => setNewItem(p => ({ ...p, description: e.target.value }))} placeholder="e.g. LED Signal Head 300mm Tri-colour" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Specification reference</label>
              <input className="input text-sm" value={newItem.specRef} onChange={e => setNewItem(p => ({ ...p, specRef: e.target.value }))} placeholder="e.g. AS/NZS 2765 cl. 4.2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Supplier ref / part number</label>
              <input className="input text-sm" value={newItem.supplierRef} onChange={e => setNewItem(p => ({ ...p, supplierRef: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
              <input type="number" min={1} className="input text-sm" value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: parseFloat(e.target.value) || 1 }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Unit</label>
              <select className="input text-sm" value={newItem.unit} onChange={e => setNewItem(p => ({ ...p, unit: e.target.value }))}>
                {['each','m','m²','m³','kg','set','lot','lm'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Unit cost (ex GST)</label>
              <div className="flex items-center gap-1"><span className="text-sm text-gray-500">$</span><input type="number" className="input text-sm" value={newItem.unitCostAud} onChange={e => setNewItem(p => ({ ...p, unitCostAud: parseFloat(e.target.value) || 0 }))} /></div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={addItem} disabled={!newItem.description} className="btn-primary text-sm disabled:opacity-50">Save & run compliance check</button>
          </div>
        </div>
      )}

      {/* BOM table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-max">
            <thead>
              <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium">Description</th>
                <th className="text-left px-3 py-3 font-medium">Spec ref</th>
                <th className="text-right px-3 py-3 font-medium">Qty</th>
                <th className="text-left px-3 py-3 font-medium">Unit</th>
                <th className="text-left px-3 py-3 font-medium">Compliance</th>
                <th className="text-left px-3 py-3 font-medium">Stock</th>
                <th className="text-right px-3 py-3 font-medium">Unit cost</th>
                <th className="text-right px-4 py-3 font-medium">Line total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(item => {
                const cm = COMPLIANCE_META[item.complianceFlag]
                const ss = STOCK_STATUS_META[item.stockStatus]
                const lineTotal = item.quantity * item.unitCostAud
                return (
                  <tr key={item.id} className={`hover:bg-gray-50 ${item.complianceFlag === 'NON_COMPLIANT' ? 'bg-red-50/30' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.description}</div>
                      {item.complianceNote && (
                        <div className="text-xs text-amber-700 mt-0.5 max-w-xs">{item.complianceNote.slice(0, 70)}{item.complianceNote.length > 70 ? '…' : ''}</div>
                      )}
                      {item.taskTitle && <div className="text-xs text-gray-400 mt-0.5">→ {item.taskTitle}</div>}
                      {item.whs && <span className="text-xs bg-red-50 text-red-600 px-1 py-0.5 rounded mt-0.5 inline-block"><Shield className="w-2.5 h-2.5 inline mr-0.5" />{item.whs.replace(/_/g,' ')}</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500 max-w-28 truncate" title={item.specRef ?? ''}>{item.specRef ?? '—'}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{item.quantity}</td>
                    <td className="px-3 py-3 text-xs text-gray-600">{item.unit}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${cm.bg} ${cm.text}`}>{cm.label}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-700">{formatCost(item.unitCostAud)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums font-semibold ${item.complianceFlag === 'NON_COMPLIANT' ? 'text-red-500 line-through' : 'text-gray-900'}`}>
                      {formatCost(lineTotal)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between text-sm">
          <span className="text-gray-500 text-xs">{filtered.length} items shown · Compliant items only included in estimate</span>
          <div className="font-bold text-gray-900">
            Estimated materials: {formatCost(totalCost)}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 4 — RFIs
// ─────────────────────────────────────────────────────────────────
function RFIsTab({ jobId, canEdit }: { jobId: string; canEdit: boolean }) {
  const [rfis, setRfis]   = useState<RFI[]>(MOCK_RFIS)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [showNew, setShowNew]   = useState(false)
  const [newRFI, setNewRFI]     = useState({ title: '', description: '', specClauseRef: '', pageRef: '', clientContact: '', clientEmail: '' })

  const jobRfis   = rfis.filter(r => r.jobId === jobId)
  const overdue   = jobRfis.filter(r => r.status === 'OVERDUE').length
  const open      = jobRfis.filter(r => ['SENT','OVERDUE'].includes(r.status)).length

  const sendRFI = (id: string) => setRfis(prev => prev.map(r => r.id === id ? { ...r, status: 'SENT', sentAt: new Date().toISOString() } : r))
  const closeRFI = (id: string) => setRfis(prev => prev.map(r => r.id === id ? { ...r, status: 'CLOSED', closedAt: new Date().toISOString() } : r))

  const createRFI = () => {
    const job = MOCK_JOBS.find(j => j.id === jobId)
    const seq = jobRfis.length + 1
    setRfis(prev => [...prev, {
      id: `rfi_${Date.now()}`, reference: `RFI-${job?.jobNumber?.replace('TP-','TP')}-${String(seq).padStart(3,'0')}`,
      jobId, jobNumber: job?.jobNumber ?? '',
      linkedDocId: null, pageRef: newRFI.pageRef || null, specClauseRef: newRFI.specClauseRef || null,
      title: newRFI.title, description: newRFI.description,
      raisedById: 'u_current', raisedByName: 'Current User', raisedAt: new Date().toISOString(),
      sentAt: null, dueDate: null,
      clientContactName: newRFI.clientContact || null, clientContactEmail: newRFI.clientEmail || null,
      responseDate: null, responseText: null, responseDocId: null,
      status: 'DRAFT', closedAt: null, closedById: null, aiPrePopulated: false,
    }])
    setShowNew(false)
    setNewRFI({ title: '', description: '', specClauseRef: '', pageRef: '', clientContact: '', clientEmail: '' })
  }

  return (
    <div className="space-y-4">
      {overdue > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800 font-medium">{overdue} RFI{overdue !== 1 ? 's' : ''} overdue — client response required. Dashboard commercial alert raised.</p>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-gray-600">
          {jobRfis.length} RFI{jobRfis.length !== 1 ? 's' : ''} · {open} open · {jobRfis.filter(r => r.status === 'CLOSED').length} closed
        </div>
        {canEdit && (
          <button onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" />New RFI
          </button>
        )}
      </div>

      {/* Phase 4: Procurement blocks from open RFIs */}
      {getRFIProcurementBlocks(jobId).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
            <AlertTriangle className="w-3.5 h-3.5" />
            Procurement blocks — these open RFIs are preventing materials from being ordered
          </div>
          {getRFIProcurementBlocks(jobId).map(block => (
            <div key={block.rfiId} className="flex items-center gap-2 text-xs text-amber-700 bg-white border border-amber-200 rounded-lg px-3 py-2">
              <span className={`px-1.5 py-0.5 rounded-full font-bold text-[10px] ${block.status === 'OVERDUE' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{block.status}</span>
              <span className="font-mono">{block.rfiReference}</span>
              <span className="text-gray-600">·</span>
              <span className="font-medium text-gray-800 truncate">{block.rfiTitle}</span>
              <span className="text-gray-400 ml-auto flex-shrink-0">Blocking: {block.bomLineDescription.split(' ').slice(0, 4).join(' ')}…</span>
            </div>
          ))}
          <p className="text-xs text-amber-700 italic">Close or resolve these RFIs to unblock procurement for the affected BOM lines.</p>
        </div>
      )}

      {showNew && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-blue-900 text-sm">Create new RFI</h4>
            <span className="text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded-full flex items-center gap-1"><Zap className="w-2.5 h-2.5" />AI pre-populated from drawing context</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">RFI title <span className="text-red-500">*</span></label>
              <input className="input text-sm" value={newRFI.title} onChange={e => setNewRFI(p => ({ ...p, title: e.target.value }))} placeholder="Concise title of the clarification needed…" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Drawing / spec page ref</label>
              <input className="input text-sm" value={newRFI.pageRef} onChange={e => setNewRFI(p => ({ ...p, pageRef: e.target.value }))} placeholder="e.g. Sheet 3 detail B" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Spec clause reference</label>
              <input className="input text-sm" value={newRFI.specClauseRef} onChange={e => setNewRFI(p => ({ ...p, specClauseRef: e.target.value }))} placeholder="e.g. MRTS50 cl. 6.3.4" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
              <textarea className="input text-sm h-24 resize-none" value={newRFI.description} onChange={e => setNewRFI(p => ({ ...p, description: e.target.value }))} placeholder="Describe the query, ambiguity, or conflict clearly. Include context from the drawing." />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Client CM contact name</label>
              <input className="input text-sm" value={newRFI.clientContact} onChange={e => setNewRFI(p => ({ ...p, clientContact: e.target.value }))} placeholder="e.g. John Barker" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Client email</label>
              <input type="email" className="input text-sm" value={newRFI.clientEmail} onChange={e => setNewRFI(p => ({ ...p, clientEmail: e.target.value }))} placeholder="e.g. john.barker@tmr.qld.gov.au" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={createRFI} disabled={!newRFI.title || !newRFI.description} className="btn-primary text-sm disabled:opacity-50">Save as draft</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {jobRfis.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl py-12 text-center text-gray-400 text-sm">No RFIs for this job</div>
        )}
        {jobRfis.map(rfi => {
          const sm = RFI_STATUS_META[rfi.status]
          const isExp = expanded === rfi.id
          const daysSent = rfi.sentAt ? Math.floor((Date.now() - new Date(rfi.sentAt).getTime()) / 86400000) : null
          return (
            <div key={rfi.id} className={`bg-white border rounded-xl overflow-hidden ${rfi.status === 'OVERDUE' ? 'border-red-300' : 'border-gray-200'}`}>
              <button onClick={() => setExpanded(isExp ? null : rfi.id)} className="w-full flex items-start gap-4 px-5 py-4 text-left hover:bg-gray-50/50">
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${rfi.status === 'OVERDUE' ? 'bg-red-600 animate-pulse' : rfi.status === 'CLOSED' ? 'bg-green-500' : 'bg-blue-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-xs font-mono text-gray-500">{rfi.reference}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                    {rfi.aiPrePopulated && <span className="text-xs bg-blue-50 text-blue-600 px-1 py-0.5 rounded flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />AI</span>}
                  </div>
                  <div className="font-semibold text-gray-900 text-sm">{rfi.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Raised {rfi.raisedByName} · {timeAgo(rfi.raisedAt)}
                    {rfi.pageRef && ` · ${rfi.pageRef}`}
                    {daysSent !== null && ` · ${daysSent} day${daysSent !== 1 ? 's' : ''} since sent`}
                    {rfi.clientContactName && ` · → ${rfi.clientContactName}`}
                  </div>
                </div>
                {isExp ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              </button>

              {isExp && (
                <div className="border-t px-5 py-4 space-y-3">
                  <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{rfi.description}</div>
                  {rfi.responseText && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="text-xs font-semibold text-green-800 mb-1">Client response — {rfi.responseDate ? fmtDate(rfi.responseDate) : ''}</div>
                      <div className="text-sm text-green-900">{rfi.responseText}</div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {rfi.status === 'DRAFT' && canEdit && (
                        <button onClick={() => sendRFI(rfi.id)} className="flex items-center gap-1.5 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1.5 rounded-lg font-medium">
                          <Send className="w-3.5 h-3.5" />Generate RFI PDF & send
                        </button>
                      )}
                      {['RESPONSE_RECEIVED','SENT'].includes(rfi.status) && canEdit && (
                        <button onClick={() => closeRFI(rfi.id)} className="flex items-center gap-1.5 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-3 py-1.5 rounded-lg font-medium">
                          <CheckCircle className="w-3.5 h-3.5" />Close RFI
                        </button>
                      )}
                    </div>
                    <button className="flex items-center gap-1.5 text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 px-3 py-1.5 rounded-lg">
                      <Download className="w-3.5 h-3.5" />Download RFI PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 5 — VARIATIONS & REVISIONS
// ─────────────────────────────────────────────────────────────────
function VariationsTab({ jobId, canEdit }: { jobId: string; canEdit: boolean }) {
  const { user } = useAuthStore()
  const [vars, setVars]   = useState<VariationRequest[]>(MOCK_VARS)
  const [changes, setChanges] = useState<RevisionChange[]>(MOCK_REVISION_CHANGES)
  const [subTab, setSubTab]   = useState<'vars'|'revisions'>('vars')
  const [showNewVar, setShowNewVar] = useState(false)
  const [newVar, setNewVar] = useState({ title: '', description: '', estimatedLabour: 0, estimatedMaterials: 0, delayDays: 0 })

  const jobVars    = vars.filter(v => v.jobId === jobId)
  const jobChanges = changes.filter(c => MOCK_REVISION_CHANGES.some(rc => rc.id === c.id && MOCK_DOCUMENTS.find(d => d.id === rc.revisionDocId && d.jobId === jobId)))
  const allChanges = changes.filter(c => {
    const revDoc = MOCK_DOCUMENTS.find(d => d.id === c.revisionDocId)
    return revDoc?.jobId === jobId
  })
  const pendingChanges = allChanges.filter(c => !c.approved).length
  const scopeChanges   = allChanges.filter(c => c.aiClassification === 'SCOPE_CHANGE' || c.aiClassification === 'MAJOR_SCOPE_CHANGE').length

  const submitVar = (id: string) => setVars(prev => prev.map(v => v.id === id ? { ...v, status: 'SUBMITTED', submittedAt: new Date().toISOString() } : v))

  const [propagationResults, setPropagationResults] = useState<Record<string, VARPropagationResult>>({})

  const approveAndApplyVar = (varId: string) => {
    const v = vars.find(x => x.id === varId)
    if (!v) return
    const result = applyVARtoBOM(v as any, user?.fullName ?? 'PM/PC')
    setPropagationResults(prev => ({ ...prev, [varId]: result }))
    setVars(prev => prev.map(x => x.id === varId ? { ...x, status: 'APPROVED', approvedAt: new Date().toISOString(), approvedBy: user?.fullName ?? 'PM/PC', bomDeltasApplied: true, bomDeltasAppliedAt: new Date().toISOString() } : x))
  }

  const rfiBlocks = getRFIProcurementBlocks(jobId)

  const approveChange = (id: string) => setChanges(prev => prev.map(c => c.id === id ? { ...c, approved: true, approvedById: 'u_current', approvedByName: 'Current User' } : c))

  const overrideClass = (id: string, cls: ChangeClassification) => setChanges(prev => prev.map(c => c.id === id ? { ...c, pmOverrideClassification: cls } : c))

  const createVar = () => {
    const job = MOCK_JOBS.find(j => j.id === jobId)
    setVars(prev => [...prev, {
      id: `var_${Date.now()}`, reference: `VAR-${job?.jobNumber?.slice(-3)}-${String(jobVars.length + 1).padStart(3,'0')}`,
      jobId, jobNumber: job?.jobNumber ?? '', type: 'PM_INITIATED',
      linkedDocId: null, linkedRfiId: null,
      title: newVar.title, scopeDescription: newVar.description,
      raisedById: 'u_current', raisedByName: 'Current User', raisedAt: new Date().toISOString(),
      submittedAt: null, status: 'DRAFT',
      approvedAt: null, approvedBy: null, rejectedAt: null, rejectionReason: null,
      estimatedLabourCost: newVar.estimatedLabour, estimatedMaterialCost: newVar.estimatedMaterials,
      estimatedDelayDays: newVar.delayDays, totalEstimatedCost: newVar.estimatedLabour + newVar.estimatedMaterials,
      pmAdjustedCost: null, pmNotes: null, clientContactName: null,
    }])
    setShowNewVar(false)
    setNewVar({ title: '', description: '', estimatedLabour: 0, estimatedMaterials: 0, delayDays: 0 })
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setSubTab('vars')}
          className={`text-sm font-medium px-4 py-1.5 rounded-lg ${subTab === 'vars' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Variations ({jobVars.length})
        </button>
        <button onClick={() => setSubTab('revisions')}
          className={`text-sm font-medium px-4 py-1.5 rounded-lg ${subTab === 'revisions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Revision comparison {pendingChanges > 0 && <span className="ml-1 bg-amber-500 text-white text-xs rounded-full px-1.5">({pendingChanges})</span>}
        </button>
      </div>

      {/* VARS */}
      {subTab === 'vars' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            {canEdit && <button onClick={() => setShowNewVar(true)} className="btn-primary flex items-center gap-2 text-sm"><Plus className="w-4 h-4" />New VAR</button>}
          </div>
          {showNewVar && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-amber-900 text-sm">Create variation approval request</h4>
                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full flex items-center gap-1"><Zap className="w-2.5 h-2.5" />Estimator AI will calculate cost impact</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                  <input className="input text-sm" value={newVar.title} onChange={e => setNewVar(p => ({ ...p, title: e.target.value }))} placeholder="Brief title of the variation…" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Scope description <span className="text-red-500">*</span></label>
                  <textarea className="input text-sm h-20 resize-none" value={newVar.description} onChange={e => setNewVar(p => ({ ...p, description: e.target.value }))} placeholder="Describe the out-of-scope work in detail…" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Est. labour cost ($)</label>
                  <input type="number" className="input text-sm" value={newVar.estimatedLabour} onChange={e => setNewVar(p => ({ ...p, estimatedLabour: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Est. materials cost ($)</label>
                  <input type="number" className="input text-sm" value={newVar.estimatedMaterials} onChange={e => setNewVar(p => ({ ...p, estimatedMaterials: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Programme delay (days)</label>
                  <input type="number" className="input text-sm" value={newVar.delayDays} onChange={e => setNewVar(p => ({ ...p, delayDays: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowNewVar(false)} className="btn-secondary text-sm">Cancel</button>
                <button onClick={createVar} disabled={!newVar.title || !newVar.description} className="btn-primary text-sm disabled:opacity-50">Create VAR draft</button>
              </div>
            </div>
          )}
          {jobVars.map(v => {
            const sm = VAR_STATUS_META[v.status]
            const totalCost = v.pmAdjustedCost ?? v.totalEstimatedCost
            return (
              <div key={v.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-mono text-gray-500">{v.reference}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${v.type === 'CLIENT_INITIATED' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {v.type === 'CLIENT_INITIATED' ? 'Client-initiated' : 'PM-initiated'}
                        </span>
                        {v.estimatedDelayDays > 0 && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">+{v.estimatedDelayDays}d delay</span>}
                      </div>
                      <div className="font-semibold text-gray-900 text-sm mb-1">{v.title}</div>
                      <p className="text-xs text-gray-600">{v.scopeDescription.slice(0, 120)}{v.scopeDescription.length > 120 ? '…' : ''}</p>
                      {v.pmNotes && <p className="text-xs text-gray-500 italic mt-1">PM note: {v.pmNotes}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold text-gray-900">{formatCost(totalCost)}</div>
                      <div className="text-xs text-gray-400">
                        Labour {formatCost(v.estimatedLabourCost)} · Materials {formatCost(v.estimatedMaterialCost)}
                        {v.pmAdjustedCost && <div className="text-blue-600">PM adjusted</div>}
                      </div>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-2 mt-3 border-t pt-3 flex-wrap">
                      {/* Phase 4: RFI procurement blocks for this VAR's affected lines */}
                      {rfiBlocks.length > 0 && v.affectedBomLines && v.affectedBomLines.some(d => d.action === 'ADD' || d.action === 'CHANGE_QTY') && (
                        <div className="w-full flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-1">
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>⚠ {rfiBlocks.length} open RFI{rfiBlocks.length !== 1 ? 's' : ''} ({rfiBlocks.map(b => b.rfiReference).join(', ')}) may affect materials in this VAR — resolve before raising procurement</span>
                        </div>
                      )}
                      {v.status === 'DRAFT' && (
                        <>
                          <button onClick={() => submitVar(v.id)} className="flex items-center gap-1.5 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1.5 rounded-lg font-medium">
                            <Send className="w-3.5 h-3.5" />Generate VAR PDF & submit
                          </button>
                          <button className="flex items-center gap-1.5 text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 px-3 py-1.5 rounded-lg">
                            <Edit className="w-3.5 h-3.5" />Edit estimate
                          </button>
                        </>
                      )}
                      {v.status === 'SUBMITTED' && canEdit && (
                        <button
                          onClick={() => approveAndApplyVar(v.id)}
                          disabled={isVARAlreadyApplied(v.id)}
                          className="flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                          <CheckCircle className="w-3.5 h-3.5" />Approve &amp; apply to BOM
                        </button>
                      )}
                      {v.status === 'APPROVED' && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                            <CheckCircle className="w-3.5 h-3.5" />Approved by {v.approvedBy} · {v.approvedAt ? fmtDate(v.approvedAt) : ''}
                          </span>
                          {/* Phase 4: show propagation result */}
                          {(() => {
                            const result = propagationResults[v.id] || (isVARAlreadyApplied(v.id) ? { linesAdded: [], linesRemoved: [], linesChanged: [], rfqFlagsRaised: 0 } : null)
                            if (!result && (v as any).bomDeltasApplied) return (
                              <span className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Package className="w-3 h-3" />BOM updated (previous session)
                              </span>
                            )
                            if (!result) return (
                              <button onClick={() => approveAndApplyVar(v.id)} className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-lg hover:bg-indigo-100 flex items-center gap-1">
                                <Package className="w-3 h-3" />Apply BOM changes now
                              </button>
                            )
                            return (
                              <span className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Package className="w-3 h-3" />BOM: +{result.linesAdded.length} added · {result.linesChanged.length} changed · {result.linesRemoved.length} removed{result.rfqFlagsRaised > 0 ? ` · ${result.rfqFlagsRaised} need RFQ` : ''}
                              </span>
                            )
                          })()}
                        </div>
                      )}
                      <button className="ml-auto flex items-center gap-1.5 text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 px-3 py-1.5 rounded-lg">
                        <Download className="w-3.5 h-3.5" />Download VAR PDF
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
          {jobVars.length === 0 && <div className="bg-white border border-gray-200 rounded-xl py-12 text-center text-gray-400 text-sm">No variation requests for this job</div>}
        </div>
      )}

      {/* REVISION COMPARISON */}
      {subTab === 'revisions' && (
        <div className="space-y-4">
          {scopeChanges > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center gap-3">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800"><strong>{scopeChanges} scope change{scopeChanges !== 1 ? 's' : ''}</strong> detected by AI in latest revision — VAR may be required.</p>
            </div>
          )}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50 flex items-center gap-2">
              <FileSearch className="w-4 h-4 text-blue-600" />
              <h3 className="font-semibold text-sm text-gray-900">AI revision comparison — Rev A vs Rev B</h3>
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{allChanges.length} changes detected</span>
            </div>
            <div className="divide-y divide-gray-100">
              {allChanges.map(ch => {
                const activeClass = ch.pmOverrideClassification ?? ch.aiClassification
                const cm = CHANGE_CLASS_META[activeClass]
                return (
                  <div key={ch.id} className="px-5 py-4 space-y-2">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${cm.bg} ${cm.text}`}>{cm.label}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-semibold text-gray-700">{ch.sheetRef}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${ch.changeType === 'ADDED' ? 'bg-green-100 text-green-700' : ch.changeType === 'REMOVED' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                            {ch.changeType}
                          </span>
                          {ch.varTriggered && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">VAR triggered → {ch.varId}</span>}
                          {ch.approved && <span className="flex items-center gap-1 text-xs text-green-700"><CheckCircle className="w-3 h-3" />Approved by {ch.approvedByName}</span>}
                        </div>
                        <p className="text-sm text-gray-700">{ch.description}</p>
                        {ch.pmAnnotation && <p className="text-xs text-blue-700 italic mt-0.5">PM note: {ch.pmAnnotation}</p>}
                        {ch.pmOverrideClassification && (
                          <p className="text-xs text-purple-700 mt-0.5">AI classified as {ch.aiClassification} → PM overridden to {ch.pmOverrideClassification}</p>
                        )}
                      </div>
                    </div>
                    {canEdit && !ch.approved && (
                      <div className="flex items-center gap-2 pl-10">
                        <span className="text-xs text-gray-500">Override classification:</span>
                        {(['MINOR','SCOPE_CHANGE','MAJOR_SCOPE_CHANGE'] as ChangeClassification[]).map(cls => (
                          <button key={cls} onClick={() => overrideClass(ch.id, cls)}
                            className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${activeClass === cls ? `${CHANGE_CLASS_META[cls].bg} ${CHANGE_CLASS_META[cls].text} border-transparent font-medium` : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                            {CHANGE_CLASS_META[cls].label}
                          </button>
                        ))}
                        <button onClick={() => approveChange(ch.id)} className="ml-auto text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />Approve change
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
              {allChanges.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No revision comparisons for this job yet</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────
export function Drawings() {
  const { user, isTier, scopedJobIds } = useAuthStore()
  const jIds = scopedJobIds()
  const [tab, setTab] = useState<'docs'|'tasks'|'bom'|'rfis'|'variations'>('docs')
  const defaultJob = jIds !== null ? (jIds[0] ?? 'j1') : 'j1'
  const [jobId, setJobId] = useState(defaultJob)

  if (!user) return null

  const canEdit = isTier('B_PC') || isTier('B_PM')  // isJobOwner not yet in Clerk store — fall back to tier check

  // Counts for badges
  const pendingDocs = MOCK_DOCUMENTS.filter(d => d.jobId === jobId && d.status === 'DRAFT_PENDING_REVIEW').length
  const blockedTasks = MOCK_TASKS.filter(t => t.jobId === jobId && t.status === 'BLOCKED').length
  const nonCompliant = MOCK_BOM.filter(b => b.jobId === jobId && b.complianceFlag === 'NON_COMPLIANT').length
  const overdueRfis  = MOCK_RFIS.filter(r => r.jobId === jobId && r.status === 'OVERDUE').length
  const pendingVars  = MOCK_VARS.filter(v => v.jobId === jobId && v.status === 'SUBMITTED').length

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Drawings &amp; Specs</h1>
          <p className="text-sm text-gray-500 mt-0.5">AI-powered document analysis · task extraction · BOM · RFIs · variations</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
            <Zap className="w-3.5 h-3.5" />Drawings &amp; Specs AI agent active
          </div>
          <JobSelector jobId={jobId} setJobId={setJobId} scopedJobIds={jIds} />
        </div>
      </div>

      {!canEdit && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl px-5 py-3 flex items-center gap-3 text-sm text-sky-800">
          <Eye className="w-4 h-4 text-sky-600 flex-shrink-0" />
          Read-only view — this job is managed by another coordinator. Contact your PM to update.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
        {([
          { key: 'docs',       label: `Documents${pendingDocs > 0 ? ` (${pendingDocs})` : ''}` },
          { key: 'tasks',      label: `AI Task List${blockedTasks > 0 ? ` ⚠${blockedTasks}` : ''}` },
          { key: 'bom',        label: `BOM${nonCompliant > 0 ? ` ✗${nonCompliant}` : ''}` },
          { key: 'rfis',       label: `RFIs${overdueRfis > 0 ? ` ⚠${overdueRfis}` : ''}` },
          { key: 'variations', label: `Variations${pendingVars > 0 ? ` (${pendingVars})` : ''}` },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'docs'       && <DocumentsTab  jobId={jobId} canEdit={canEdit} />}
      {tab === 'tasks'      && <TasksTab      jobId={jobId} canEdit={canEdit} />}
      {tab === 'bom'        && <BOMTab        jobId={jobId} canEdit={canEdit} />}
      {tab === 'rfis'       && <RFIsTab       jobId={jobId} canEdit={canEdit} />}
      {tab === 'variations' && <VariationsTab jobId={jobId} canEdit={canEdit} />}
    </div>
  )
}
